import React, { useState, useEffect } from 'react';
import './App.css';

const STRINGS = {
  en: {
    title: "Mirinae's Wishlist",
    loading: 'Loading...',
    wishlistItems: 'Wishlist Items',
    available: 'available',
    filter: 'Filter',
    allCategories: 'All Categories',
    empty: 'No items yet. Add some gifts above!',
    bought: 'I bought this!',
    allPurchased: 'All Purchased',
    purchasedOf: (n, total) => `${n} of ${total} purchased`,
    confirmPurchase: 'Confirm Purchase',
    confirmText: (title) => <>You're about to mark <strong>{title}</strong> as purchased!</>,
    yourName: 'Your name (optional)',
    yourMessage: 'Leave a message for Mirinae (optional)',
    cancel: 'Cancel',
    footer: 'Made with love for Mirinae',
    adminLogin: 'Admin Login',
    enterPassword: 'Enter password',
    login: 'Login',
    showPurchased: 'Show purchased items',
    hidePurchased: 'Hide purchased items',
    allHidden: 'All items have been purchased. Use the eye icon to show them.',
    categories: {},
    linkText: {}
  },
  ko: {
    title: '미리내의 위시리스트',
    loading: '불러오는 중...',
    wishlistItems: '위시리스트',
    available: '개 남음',
    filter: '필터',
    allCategories: '전체',
    empty: '아직 선물이 없어요. 위에서 추가해 주세요!',
    bought: '제가 샀어요!',
    allPurchased: '모두 구매 완료',
    purchasedOf: (n, total) => `${total}개 중 ${n}개 구매`,
    confirmPurchase: '구매 확인',
    confirmText: (title) => <><strong>{title}</strong>을(를) 구매한 것으로 표시할게요!</>,
    yourName: '이름 (선택)',
    yourMessage: '미리내에게 메시지를 남겨주세요 (선택)',
    cancel: '취소',
    footer: '미리내를 위해 사랑으로 만들었어요',
    adminLogin: '관리자 로그인',
    enterPassword: '비밀번호 입력',
    login: '로그인',
    showPurchased: '구매한 선물 보기',
    hidePurchased: '구매한 선물 숨기기',
    allHidden: '모든 선물이 구매되었어요. 눈 아이콘을 눌러 보세요.',
    categories: {
      'Books': '책', 'Toys': '장난감', 'Gift Cards': '기프트카드', 'Clothes': '옷',
      'Subscriptions': '구독', 'Classes': '수업', 'Tickets': '티켓',
      'Memberships': '멤버십', 'Cash Gift': '용돈', 'Other': '기타'
    },
    linkText: {
      'Books': '책 보기', 'Toys': '장난감 보기', 'Gift Cards': '기프트카드 보기', 'Clothes': '상품 보기',
      'Subscriptions': '구독 보기', 'Classes': '수업 보기', 'Tickets': '티켓 보기',
      'Memberships': '멤버십 보기', 'Cash Gift': '자세히 보기', 'Other': '상품 보기'
    }
  }
};

function App() {
  const [showPurchased, setShowPurchased] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const t = STRINGS[lang];
  const tc = (cat) => t.categories[cat] || cat;
  const isVisible = (item) =>
    (selectedCategory === 'All' || item.category === selectedCategory) &&
    (showPurchased || !item.purchased);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({ title: '', link: '', quantity: 1, category: 'Books', price: '', secondaryCategory: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedItemToPurchase, setSelectedItemToPurchase] = useState(null);
  const [purchaserName, setPurchaserName] = useState('');
  const [purchaserMessage, setPurchaserMessage] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [fetchingImageFor, setFetchingImageFor] = useState(null);
  const [purchasedSectionCollapsed, setPurchasedSectionCollapsed] = useState(true);

  const API_URL = process.env.NODE_ENV === 'production' 
    ? '/api' 
    : `http://${window.location.hostname}:3001/api`;
  const apiFetch = (url, options = {}) => fetch(url, { ...options, credentials: 'include' });

  const categories = ['Books', 'Toys', 'Gift Cards', 'Clothes', 'Subscriptions', 'Classes', 'Tickets', 'Memberships', 'Cash Gift', 'Other'];
  const secondaryCategories = ['Books', 'Toys', 'Gift Cards', 'Clothes', 'Subscriptions', 'Classes', 'Tickets', 'Memberships', 'Other'];

  const getCategoryLinkText = (category) => {
    if (t.linkText[category]) return t.linkText[category];
    const linkText = {
      'Books': 'Buy Book',
      'Toys': 'Buy Toy',
      'Gift Cards': 'Buy Gift Card',
      'Clothes': 'Buy Item',
      'Subscriptions': 'Buy Subscription',
      'Classes': 'Buy Class',
      'Tickets': 'Buy Ticket',
      'Memberships': 'Buy Membership',
      'Cash Gift': 'More info',
      'Other': 'View Product'
    };
    return linkText[category] || 'View Product';
  };

  useEffect(() => {
    localStorage.setItem('lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    try {
      const response = await apiFetch(`${API_URL}/items`);
      const data = await response.json();
      setItems(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching items:', error);
      setIsLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!newItem.title.trim()) {
      alert('Please enter a title');
      return;
    }

    try {
      const response = await apiFetch(`${API_URL}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem)
      });
      
      const data = await response.json();
      setItems([data, ...items]);
      setNewItem({ title: '', link: '', quantity: 1, category: 'Books', price: '', secondaryCategory: '' });

      // If the item has a link, poll for the image after the server's background fetch completes
      if (data.link) {
        const pollForImage = async (attempts = 0) => {
          if (attempts >= 5) return; // give up after ~15 seconds
          await new Promise(r => setTimeout(r, 3000));
          try {
            const refreshed = await apiFetch(`${API_URL}/items`).then(r => r.json());
            const updated = refreshed.find(i => i.id === data.id);
            if (updated?.image_url) {
              setItems(prev => prev.map(i => i.id === data.id ? updated : i));
            } else {
              pollForImage(attempts + 1);
            }
          } catch (e) { /* silently ignore */ }
        };
        pollForImage();
      }
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handlePurchaseClick = (item) => {
    setSelectedItemToPurchase(item);
    setShowPurchaseModal(true);
  };

  const handlePurchaseConfirm = async () => {
    if (!selectedItemToPurchase) return;

    try {
      const response = await apiFetch(`${API_URL}/items/${selectedItemToPurchase.id}/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          purchaserName: purchaserName.trim(),
          purchaserMessage: purchaserMessage.trim()
        })
      });
      
      const updatedItem = await response.json();
      setItems(items.map(item => item.id === selectedItemToPurchase.id ? updatedItem : item));
      
      // Close modal and reset
      setShowPurchaseModal(false);
      setSelectedItemToPurchase(null);
      setPurchaserName('');
      setPurchaserMessage('');
    } catch (error) {
      console.error('Error marking as purchased:', error);
      alert('Error processing purchase. Please try again.');
    }
  };

  const handlePurchaseCancel = () => {
    setShowPurchaseModal(false);
    setSelectedItemToPurchase(null);
    setPurchaserName('');
    setPurchaserMessage('');
  };

  const handleEditClick = (item) => {
    setEditingItem({...item});
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editingItem || !editingItem.title.trim()) {
      alert('Please enter a title');
      return;
    }

    try {
      const response = await apiFetch(`${API_URL}/items/${editingItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingItem)
      });
      
      const updatedItem = await response.json();
      setItems(items.map(item => item.id === updatedItem.id ? updatedItem : item));
      
      setShowEditModal(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Error updating item. Please try again.');
    }
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingItem(null);
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Are you sure you want to restore this item and remove all purchase records?')) {
      return;
    }

    try {
      const response = await apiFetch(`${API_URL}/items/${id}/restore`, {
        method: 'POST'
      });
      
      const updatedItem = await response.json();
      setItems(items.map(item => item.id === id ? updatedItem : item));
    } catch (error) {
      console.error('Error restoring item:', error);
      alert('Error restoring item. Please try again.');
    }
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = async (e, targetItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    // Get the currently visible/filtered items for reordering
    const visibleItems = items.filter(isVisible);
    const draggedIndex = visibleItems.findIndex(item => item.id === draggedItem.id);
    const targetIndex = visibleItems.findIndex(item => item.id === targetItem.id);

    // Reorder only the visible items
    const newVisibleItems = [...visibleItems];
    newVisibleItems.splice(draggedIndex, 1);
    newVisibleItems.splice(targetIndex, 0, draggedItem);

    // Update the full items array, preserving non-visible items in their original positions
    const newItems = [...items];
    const visibleItemsMap = new Map(newVisibleItems.map((item, index) => [item.id, index]));
    
    // Sort all items: visible items get new order, others keep relative positions
    newItems.sort((a, b) => {
      const aIsVisible = visibleItemsMap.has(a.id);
      const bIsVisible = visibleItemsMap.has(b.id);
      
      if (aIsVisible && bIsVisible) {
        return visibleItemsMap.get(a.id) - visibleItemsMap.get(b.id);
      }
      if (aIsVisible && !bIsVisible) return -1;
      if (!aIsVisible && bIsVisible) return 1;
      return a.order - b.order; // Keep original order for non-visible items
    });

    // Update local state immediately for responsive UI
    setItems(newItems);
    setDraggedItem(null);

    // Send the new order to the backend to persist it
    try {
      const itemIds = newItems.map(item => item.id);
      console.log('🔄 Sending reorder request:', itemIds);
      const response = await apiFetch(`${API_URL}/items/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds })
      });
      
      if (!response.ok) {
        console.error('❌ Failed to save new order, response status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      } else {
        const result = await response.json();
        console.log('✅ Order saved successfully:', result.length, 'items');
      }
    } catch (error) {
      console.error('❌ Error saving new order:', error);
    }
  };

  const handleTouchStart = (e, item) => {
    if (!isAdmin) return;
    setDraggedItem(item);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
  };

  const handleTouchEnd = async (e, targetItem) => {
    if (!draggedItem || !targetItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    // Get the currently visible/filtered items for reordering
    const visibleItems = items.filter(isVisible);
    const draggedIndex = visibleItems.findIndex(item => item.id === draggedItem.id);
    const targetIndex = visibleItems.findIndex(item => item.id === targetItem.id);

    // Reorder only the visible items
    const newVisibleItems = [...visibleItems];
    newVisibleItems.splice(draggedIndex, 1);
    newVisibleItems.splice(targetIndex, 0, draggedItem);

    // Update the full items array, preserving non-visible items in their original positions
    const newItems = [...items];
    const visibleItemsMap = new Map(newVisibleItems.map((item, index) => [item.id, index]));
    
    // Sort all items: visible items get new order, others keep relative positions
    newItems.sort((a, b) => {
      const aIsVisible = visibleItemsMap.has(a.id);
      const bIsVisible = visibleItemsMap.has(b.id);
      
      if (aIsVisible && bIsVisible) {
        return visibleItemsMap.get(a.id) - visibleItemsMap.get(b.id);
      }
      if (aIsVisible && !bIsVisible) return -1;
      if (!aIsVisible && bIsVisible) return 1;
      return a.order - b.order; // Keep original order for non-visible items
    });

    // Update local state immediately for responsive UI
    setItems(newItems);
    setDraggedItem(null);

    // Send the new order to the backend to persist it
    try {
      const itemIds = newItems.map(item => item.id);
      console.log('🔄 Sending touch reorder request:', itemIds);
      const response = await apiFetch(`${API_URL}/items/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds })
      });
      
      if (!response.ok) {
        console.error('❌ Failed to save new order (touch), response status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
      } else {
        const result = await response.json();
        console.log('✅ Touch order saved successfully:', result.length, 'items');
      }
    } catch (error) {
      console.error('❌ Error saving new order (touch):', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await apiFetch(`${API_URL}/items/${id}`, {
          method: 'DELETE'
        });
        setItems(items.filter(item => item.id !== id));
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleFetchImage = async (item) => {
    setFetchingImageFor(item.id);
    try {
      const response = await apiFetch(`${API_URL}/items/${item.id}/fetch-image`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Could not find an image for this item.');
      } else {
        setItems(items.map(i => i.id === item.id ? data : i));
      }
    } catch (error) {
      console.error('Error fetching image:', error);
      alert('Error fetching image. Please try again.');
    } finally {
      setFetchingImageFor(null);
    }
  };

  const handleClearImage = async (item) => {
    try {
      const response = await apiFetch(`${API_URL}/items/${item.id}/image`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_url: null })
      });
      const data = await response.json();
      setItems(items.map(i => i.id === item.id ? data : i));
    } catch (error) {
      console.error('Error clearing image:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiFetch(`${API_URL}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAdmin(true);
        setShowPasswordPrompt(false);
        setPassword('');
      } else {
        alert('Invalid password');
        setPassword('');
      }
    } catch (error) {
      console.error('Error verifying password:', error);
      alert('Error verifying password');
    }
  };

  const handleLogout = async () => {
    await apiFetch(`${API_URL}/auth/logout`, { method: 'POST' });
    setIsAdmin(false);
  };

  if (isLoading) {
    return <div className="loading">{t.loading}</div>;
  }

  return (
    <div className={`App lang-${lang}`}>
      <header className="header">
        <div className="lang-toggle" role="group" aria-label="Language">
          <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>EN</button>
          <button className={lang === 'ko' ? 'active' : ''} onClick={() => setLang('ko')}>한국어</button>
        </div>
        <div className="header-content">
          <div className="header-text">
            <h1>
              <span
                className={`admin-star ${isAdmin ? 'is-admin' : ''}`}
                onClick={() => !isAdmin ? setShowPasswordPrompt(true) : handleLogout()}
                title={isAdmin ? "Logout" : "Admin"}
              >
                {isAdmin ? '★' : '✦'}
              </span>
              {' '}{t.title}
            </h1>
            <p className="name-ko">미리내</p>
          </div>
        </div>
      </header>

      {/* Password prompt modal */}
      {showPasswordPrompt && (
        <div className="modal-overlay" onClick={() => setShowPasswordPrompt(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t.adminLogin}</h2>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                placeholder={t.enterPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                autoFocus
              />
              <div className="modal-buttons">
                <button type="submit" className="btn btn-primary">{t.login}</button>
                <button 
                  type="button" 
                  className="btn btn-cancel"
                  onClick={() => {
                    setShowPasswordPrompt(false);
                    setPassword('');
                  }}
                >
                  {t.cancel}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Purchase confirmation modal */}
      {showPurchaseModal && selectedItemToPurchase && (
        <div className="modal-overlay" onClick={handlePurchaseCancel}>
          <div className="modal-content purchase-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t.confirmPurchase}</h2>
            <p className="purchase-confirm-text">
              {t.confirmText(selectedItemToPurchase.title)}
            </p>
            <div className="purchase-form">
              <input
                type="text"
                placeholder={t.yourName}
                value={purchaserName}
                onChange={(e) => setPurchaserName(e.target.value)}
                className="input-field"
              />
              <textarea
                placeholder={t.yourMessage}
                value={purchaserMessage}
                onChange={(e) => setPurchaserMessage(e.target.value)}
                className="input-field message-textarea"
                rows="3"
              />
            </div>
            <div className="modal-buttons">
              <button 
                type="button"
                className="btn btn-primary"
                onClick={handlePurchaseConfirm}
              >
                {t.confirmPurchase}
              </button>
              <button 
                type="button" 
                className="btn btn-cancel"
                onClick={handlePurchaseCancel}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit item modal */}
      {showEditModal && editingItem && (
        <div className="modal-overlay" onClick={handleEditCancel}>
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <h2>✎ Edit Item</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleEditSave(); }}>
              <input
                type="text"
                placeholder="Gift name or experience"
                value={editingItem.title}
                onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                className="input-field"
              />
              <input
                type="url"
                placeholder="Product link (optional)"
                value={editingItem.link || ''}
                onChange={(e) => setEditingItem({ ...editingItem, link: e.target.value })}
                className="input-field"
              />
              <div className="form-row">
                <select
                  value={editingItem.category}
                  onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                  className="input-field category-select"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  placeholder="Quantity"
                  value={editingItem.quantity}
                  onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 0 })}
                  className="input-field quantity-input"
                />
              </div>
              
              {editingItem.category === 'Cash Gift' && (
                <div className="cash-gift-fields">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Amount (e.g., 25.00)"
                    value={editingItem.price || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })}
                    className="input-field"
                  />
                  <select
                    value={editingItem.secondaryCategory || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, secondaryCategory: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Select category for this gift (optional)</option>
                    {secondaryCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="modal-buttons">
                <button type="submit" className="btn btn-primary">Save Changes</button>
                <button 
                  type="button" 
                  className="btn btn-cancel"
                  onClick={handleEditCancel}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="container">
        {isAdmin && (
          <>
            {/* Purchased Items Section */}
            <div className="purchased-items-section">
              <h2 className="collapsible-heading" onClick={() => setPurchasedSectionCollapsed(c => !c)}>
                Purchased Items & Messages
                <span className="collapse-arrow">{purchasedSectionCollapsed ? '▶' : '▼'}</span>
              </h2>
              {!purchasedSectionCollapsed && (items.filter(item => item.purchasers && item.purchasers.length > 0).length === 0 ? (
                <p className="empty-message">No purchases yet.</p>
              ) : (
                <div className="purchased-items-grid">
                  {items
                    .filter(item => item.purchasers && item.purchasers.length > 0)
                    .map((item) => (
                      <div key={item.id} className="purchased-item-card">
                        <div className="purchased-item-header">
                          <div>
                            <h3>{item.title}</h3>
                            <span className={`category-badge ${(item.category || 'Other').toLowerCase().replace(' ', '-')}`}>
                              {item.category || 'Other'}
                            </span>
                          </div>
                          {item.purchased && (
                            <button 
                              onClick={() => handleRestore(item.id)}
                              className="btn btn-restore"
                            >
                              Restore
                            </button>
                          )}
                        </div>
                        <div className="purchasers-list">
                          {item.purchasers.map((purchaser, idx) => (
                            <div key={idx} className="purchaser-info">
                              <div className="purchaser-header">
                                <strong>{purchaser.name}</strong>
                                <span className="purchase-date">
                                  {new Date(purchaser.purchasedAt).toLocaleDateString()}
                                </span>
                              </div>
                              {purchaser.message && (
                                <p className="purchaser-message">"{purchaser.message}"</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </div>

            <div className="add-item-form">
              <h2>Add New Item</h2>
          <form onSubmit={handleAddItem}>
            <input
              type="text"
              placeholder="Gift name or experience"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              className="input-field"
            />
            <input
              type="url"
              placeholder="Product link (optional)"
              value={newItem.link}
              onChange={(e) => setNewItem({ ...newItem, link: e.target.value })}
              className="input-field"
            />
            <div className="form-row">
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="input-field category-select"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="number"
                min="1"
                placeholder="Quantity"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                className="input-field quantity-input"
              />
            </div>
            
            {newItem.category === 'Cash Gift' && (
              <div className="cash-gift-fields">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Amount (e.g., 25.00)"
                  value={newItem.price}
                  onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                  className="input-field"
                />
                <select
                  value={newItem.secondaryCategory}
                  onChange={(e) => setNewItem({ ...newItem, secondaryCategory: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select category for this gift (optional)</option>
                  {secondaryCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            )}
            
            <button type="submit" className="btn btn-primary">Add to Wishlist</button>
          </form>
        </div>
          </>
        )}

        <div className="wishlist">
          <div className="wishlist-header">
            <h2>{t.wishlistItems} ({lang === 'en' ? `${items.filter(item => !item.purchased).length} ${t.available}` : `${items.filter(item => !item.purchased).length}${t.available}`})</h2>
            <div className="category-filter">
              <label>{t.filter}: </label>
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                <option value="All">{t.allCategories}</option>
                {categories
                  .filter(cat => items.some(item => item.category === cat))
                  .map(cat => (
                    <option key={cat} value={cat}>{tc(cat)}</option>
                  ))}
              </select>
              <button
                type="button"
                className={`eye-toggle ${showPurchased ? 'active' : ''}`}
                onClick={() => setShowPurchased(v => !v)}
                title={showPurchased ? t.hidePurchased : t.showPurchased}
                aria-label={showPurchased ? t.hidePurchased : t.showPurchased}
                aria-pressed={showPurchased}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                  {!showPurchased && <line x1="3" y1="3" x2="21" y2="21" />}
                </svg>
              </button>
            </div>
          </div>
          
          {items.length === 0 ? (
            <p className="empty-message">{t.empty}</p>
          ) : items.filter(isVisible).length === 0 ? (
            <p className="empty-message">{t.allHidden}</p>
          ) : (
            <div className="items-grid">
              {items
                .filter(isVisible)
                .map((item) => {
                  const totalQuantity = (item.purchasers?.length || 0) + item.quantity;
                  const purchasedCount = item.purchasers?.length || 0;
                  
                  return (
                  <div 
                    key={item.id} 
                    className={`item-card ${item.purchased ? 'purchased' : ''} ${isAdmin ? 'draggable' : ''} ${draggedItem?.id === item.id ? 'dragging' : ''}`}
                    draggable={isAdmin}
                    onDragStart={(e) => isAdmin && handleDragStart(e, item)}
                    onDragOver={(e) => isAdmin && handleDragOver(e)}
                    onDrop={(e) => isAdmin && handleDrop(e, item)}
                    onTouchStart={(e) => handleTouchStart(e, item)}
                    onTouchMove={(e) => isAdmin && handleTouchMove(e)}
                    onTouchEnd={(e) => isAdmin && handleTouchEnd(e, item)}
                  >
                    {item.image_url && (
                      <div className="item-image-wrapper">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="item-image"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {isAdmin && (
                          <button
                            className="btn-clear-image"
                            onClick={() => handleClearImage(item)}
                            title="Remove image"
                          >✕</button>
                        )}
                      </div>
                    )}
                    <div className="item-header">
                      <div className="item-header-left">
                        <div className="category-badges">
                          <span className={`category-badge ${(item.category || 'Other').toLowerCase().replace(' ', '-')}`}>
                            {tc(item.category || 'Other')}
                          </span>
                          {item.category === 'Cash Gift' && item.secondaryCategory && (
                            <span className={`category-badge ${item.secondaryCategory.toLowerCase().replace(' ', '-')}`}>
                              {tc(item.secondaryCategory)}
                            </span>
                          )}
                        </div>
                        <h3 className="item-title">{item.title}</h3>
                        {item.category === 'Cash Gift' && item.price && (
                          <div className="price-display">${parseFloat(item.price).toFixed(2)}</div>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="admin-controls">
                          {item.link && (
                            <button
                              onClick={() => handleFetchImage(item)}
                              className="btn-fetch-image"
                              title={item.image_url ? 'Re-fetch image' : 'Fetch image'}
                              disabled={fetchingImageFor === item.id}
                            >
                              {fetchingImageFor === item.id ? '⏳' : '🖼'}
                            </button>
                          )}
                          <button 
                            onClick={() => handleEditClick(item)}
                            className="btn-edit"
                            title="Edit item"
                          >
                            ✎
                          </button>
                          <button 
                            onClick={() => handleDelete(item.id)}
                            className="btn-delete"
                            title="Delete item"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {item.link && (
                      <a 
                        href={item.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="item-link"
                      >
                        {getCategoryLinkText(item.category)} →
                      </a>
                    )}
                    
                    <div className="item-footer">
                      {totalQuantity > 1 && (
                        <div className="quantity-badge">
                          {item.purchased ? (
                            <span className="status-purchased">✓ {t.allPurchased}</span>
                          ) : (
                            <span className="status-available">
                              {t.purchasedOf(purchasedCount, totalQuantity)}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {!item.purchased && !isAdmin && (
                        <button 
                          onClick={() => handlePurchaseClick(item)}
                          className="btn btn-secondary"
                        >
                          {t.bought}
                        </button>
                      )}
                      
                      {Boolean(item.purchased) && isAdmin && (
                        <button 
                          onClick={() => handleRestore(item.id)}
                          className="btn btn-restore"
                        >
                          Restore Item
                        </button>
                      )}
                    </div>
                  </div>
                )}
              )}
            </div>
          )}
        </div>
      </div>

      <footer className="footer">
        <p>{t.footer}</p>
      </footer>
    </div>
  );
}

export default App;
